import type { Maybe } from '@adornis/base/utilTypes.js';
import type { BaseQLSelectionSet, NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { subscribeWorkshopSetPaginated } from '@adornis/buchungsheld-db/api/workshop-set.js';
import { AdminInstitutionViewSetting } from '@adornis/buchungsheld-db/entities/AdminInstitutionViewSetting.js';
import { BHFilter } from '@adornis/buchungsheld-db/entities/bh-filter.js';
import { Institution } from '@adornis/buchungsheld-db/entities/institution.js';
import { WorkshopSet } from '@adornis/buchungsheld-db/entities/WorkshopSet.js';
import { dataviewEmptyState } from '@adornis/buchungsheld-db/global-helpers.js';
import { isDefined } from '@adornis/buchungsheld-db/util.js';
import { DesignSystem, type ThemeDefinition } from '@adornis/chemistry/elements/theming/design.js';
import { buildFullOptions, type DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import { View } from '@adornis/data-view/db/view.js';
import { stateful } from '@adornis/functional-lit/stateful.js';
import { webcomponent } from '@adornis/functional-lit/webcomponent.js';
import { clientTranslator } from '@adornis/translation-core/client/clientTranslator.js';
import { html } from 'lit';
import { useContextEntity } from '../../../../util.js';
import { adminEinrichtungWorkshopSetRouter } from './admin-einrichtung-workshop-set-router.js';

const workshopSetSelectionSet = {
  _id: 1,
} satisfies BaseQLSelectionSet<WorkshopSet>;

const getLsKey = (institutionID: string) => `extra_columns:${institutionID}:workshop_sets:v1`;

function getCachedExtraColumns(institutionID: string) {
  const lsKey = getLsKey(institutionID);
  const extraColumns = JSON.parse(localStorage.getItem(lsKey) ?? '[]');
  return extraColumns;
}

function setCachedExtraColumns(institutionID: string, columns: string[]) {
  const lsKey = getLsKey(institutionID);
  localStorage.setItem(lsKey, JSON.stringify(columns));
}

const dataViewConfig = (
  filter: BHFilter,
  institutionID: string,
  viewSettings?: NarrowedEntity<
    AdminInstitutionViewSetting,
    { institutionDetailsWorkshopSetsFilterV1: typeof BHFilter.allFields }
  >,
) => {
  const extraColumns = getCachedExtraColumns(institutionID).map(fieldName => ({ fieldName }));

  return {
    class: WorkshopSet,
    operation: subscribeWorkshopSetPaginated,
    selectionSet: { name: 1 },
    isSortable: true,
    isColumnSelectable: true,
    emptyState: () => dataviewEmptyState({}),
    view: new View({ filter }),
    onViewChanged: async view => {
      onViewUpdate(institutionID, dataViewConfig(filter, institutionID, viewSettings), view);
      // TODO: save filter in onSortUpdate Event or somehow ignore dataview update
      // await saveFilter(viewSettings, view.filter);
    },
    customColumns: [
      {
        fieldName: 'name',
        isSortable: true,
        columnName: 'Name',
        columnWidth: '1fr',
        selectionSet: { name: 1 },
      },
      ...extraColumns,
      {
        columnName: '',
        customID: '_options',
        renderItem: doc => renderDataViewEntryOptions({ doc }),
        columnWidth: 'max-content',
      },
    ],
  } satisfies DataViewOptions<typeof WorkshopSet>;
};

function onViewUpdate(institutionID: string, dataViewOptions: DataViewOptions<any>, view: View<any>) {
  const preExistingFields = dataViewOptions.customColumns!.map(col => col.fieldName).filter(isDefined);
  const cachedExtraFields = getCachedExtraColumns(institutionID);
  const selectionFields = view.configuredColumns!.map(col => col.fieldName).filter(isDefined);
  const newFields = selectionFields.filter(it => !preExistingFields.includes(it));
  const fieldsToBeRemoved = preExistingFields.filter(it => !selectionFields.includes(it));
  const newCachedExtraFields = cachedExtraFields.filter(it => !fieldsToBeRemoved.includes(it));
  for (const field of newFields) newCachedExtraFields.push(field);

  setCachedExtraColumns(institutionID, newCachedExtraFields);

  view.configuredColumns?.sort((col1, col2) => {
    if (col1.customID === '_select') return -1;
    if (col1.customID === '_options') return 1;
    if (col2.customID === '_select') return 1;
    if (col2.customID === '_options') return -1;
    return 0;
  });
}

async function saveFilter(
  viewSettings: Maybe<
    NarrowedEntity<AdminInstitutionViewSetting, { institutionDetailsWorkshopSetsFilterV1: typeof BHFilter.allFields }>
  >,
  filter: any,
) {
  if (!viewSettings) return;
  viewSettings.institutionDetailsWorkshopSetsFilterV1 = filter;
  await viewSettings.save();
}

export const workshopSetOverview = webcomponent('bh-page-admin-einrichtung-workshop-set-overview', () => {
  const [institution] = useContextEntity(Institution)({ _id: 1 });
  const [viewSettings] = useContextEntity(AdminInstitutionViewSetting)({
    selectedTimeframeID: 1,
    institutionDetailsWorkshopSetsFilterV1: selectionSet(() => BHFilter, 5),
  });
  const design = DesignSystem.currentTheme.getValue();

  const selectedTimeframeID = viewSettings?.selectedTimeframeID;

  if (!institution) return html` <tr-loading-state></tr-loading-state> `;

  const institutionID = institution._id;

  const _filter = { institutionID } as any;
  if (selectedTimeframeID) _filter.timeframeID = selectedTimeframeID;
  const filter = new BHFilter(_filter);

  // Apply saved filter aspects if available
  const savedAspects = viewSettings?.institutionDetailsWorkshopSetsFilterV1?.aspects;
  const savedSortFields = viewSettings?.institutionDetailsWorkshopSetsFilterV1?.sortingFields;
  const savedSortDirections = viewSettings?.institutionDetailsWorkshopSetsFilterV1?.sortingDirections;
  if (savedAspects && savedAspects.length > 0) filter.aspects = savedAspects as any;
  if (savedSortFields && savedSortFields.length > 0) filter.sortingFields = savedSortFields as any;
  if (savedSortDirections && savedSortDirections.length > 0) filter.sortingDirections = savedSortDirections as any;

  return workshopSetListing({ institutionID: institution._id, filter, design });
});

const workshopSetListing = stateful<{
  institutionID: Maybe<string>;
  filter: Maybe<BHFilter>;
  design: ThemeDefinition<any> | undefined;
}>(({ institutionID, filter, design }) => {
  const [viewSettings] = useContextEntity(AdminInstitutionViewSetting)({
    _id: 1,
    institutionDetailsWorkshopSetsFilterV1: selectionSet(() => BHFilter, 5),
  });

  if (!filter || !institutionID) return html` <tr-loading-state></tr-loading-state> `;

  const config = dataViewConfig(filter, institutionID, viewSettings);
  const { dataViewOptions, filterOptions } = buildFullOptions(config);

  return html`
    <x-flex space="md">
      <x-flex space-between space="md" wrap horizontal crossaxis-center>
        <x-h3> ${clientTranslator.translate('workshop_set', 'plural')} </x-h3>
        <tr-button-small icon="add" .action=${() => adminEinrichtungWorkshopSetRouter.goTo('add')}>
          <x-text> ${clientTranslator.translate('workshop_set', 'add')} </x-text>
        </tr-button-small>
      </x-flex>
      <x-filter-editor
        .options=${filterOptions}
        @filter-apply="${e => saveFilter(viewSettings, e.detail)},"
        @filter-reset="${() => saveFilter(viewSettings, null)},"
      ></x-filter-editor>
      <x-data-view .options=${dataViewOptions}> </x-data-view>
    </x-flex>
  `;
});

const renderDataViewEntryOptions = stateful<{ doc: NarrowedEntity<WorkshopSet, typeof workshopSetSelectionSet> }>(
  ({ doc }) => {
    const design = DesignSystem.currentTheme.getValue();

    return html`
      <x-flex space="xs" horizontal crossaxis-start>
        <tr-button-small
          icon="edit"
          .action=${() => adminEinrichtungWorkshopSetRouter.goTo('modify', { params: { workshopSetID: doc._id } })}
          .color=${design?.colors.secondary.shade(200)}
        ></tr-button-small>
      </x-flex>
    `;
  },
);
